/**
 * # I18n store
 * > Manage our locales
 *
 * !!! FLOW !!!
 *
 * wait to load local + api messages
 * then setappStore.setI18nReady
 *
 * changes must be made if do not load from api (end of `setMessages()` function)
 *
 * @author nusson <nicolas@kffein.com>
 */
import messages from '@intlify/unplugin-vue-i18n/messages';
import { until } from '@vueuse/core';
import { filter, keyBy, mapValues, merge } from 'lodash';
import { defineStore } from 'pinia';
import appStore from 'plugins/store/app';
import { store as globalsStore } from 'plugins/store/globals';
import { pinia } from 'plugins/store/index';
import { byCode, byId, byName, byUri, locales as configLocales } from 'src/config/locales';
import { computed, nextTick, ref, toRaw } from 'vue';
import { LocaleMessageDictionary, VueMessageType } from 'vue-i18n';


// those will always be loaded - like fallback
// vs globals may only use distant datas
// async function loadEnMessages(){ return import(/* @vite-ignore */ `./en`) }
// async function loadFrMessages(){ return import(/* @vite-ignore */ `./fr`) }

/** messages because */
type LocaleMessages = LocaleMessageDictionary<VueMessageType>
type Messages = {
  [localeCode: string]: LocaleMessages
}

type LoadedState = boolean | 'loading' | 'local' | 'api' // false if none, true if both
export interface I18NState extends Obj {
  locales: Koddein.Locale[]
  locale: Optional<Koddein.Locale>
  data: {
    messages: Messages,
    datetimeFormats: {
      [localeCode: string]: Obj
    }
  }
  loaded: {
    [localeCode: string]: LoadedState
  },
  ready: boolean
}

const localesCodes = keyBy(configLocales, 'code')


const useStore = defineStore('i18n', () => {

  const state = ref<I18NState>({
    loaded: mapValues(localesCodes, () => false),
    data: {
      // @ts-expect-error
      messages: messages as Messages,
      datetimeFormats: mapValues(localesCodes, () => ({})),
    },
    locales: configLocales,
    locale: undefined,
    ready: false
  });

  // Getters (here because called from other getters)
  const locales = computed( () => state.value.locales);
  const locale = computed<Optional<Koddein.Locale>>( () => state.value.locale);

  //Actions

  /**
  * update locale by... something (name, uri etc)
  * relative to our config/locales.ts config
  * (may be used in main.ts to set router with current locale)
  * ```
  *  const _locales:SimpleLocale[] = [{
  *    id: 1,
  *    name: 'fr',
  *    uri: 'fr',
  *    code: 'fr-CA',
  *  },...]
  * ```
  */
  const setLocaleByName = (name: string): void => {
    const locale = byName(name)
    if (!locale) return
    setLocale(locale)
  }
  // by code `
  const setLocaleByCode = (code: string): void => {
    const locale = byCode(code)
    if (!locale) return
    setLocale(locale)
  }
  const setLocaleByUri = (uri: string): void => {
    const locale = byUri(uri)
    if (!locale) return
    setLocale(locale)
  }


  /**
  * set locales messages - from local or api
  */
   const setMessages = (messages: LocaleMessages, localeCode: string, fromApi = true) => {
    // fromApi would override local messages
    const current = state.value.data.messages[localeCode];
    
    const merged = fromApi
      ? merge({}, toRaw(current), messages)
      : merge({}, messages, toRaw(current));
    state.value.data.messages[localeCode] = merged

    // update loading state
    const loadingState = (() => {
      const loadingState = state.value.loaded[localeCode]
      if (fromApi) return loadingState === 'local' ? true : 'api'
      return loadingState === 'api' ? true : 'local'
    })()
    state.value.loaded[localeCode] = loadingState
    
    // ready when both local AND api loaded for current locale
    setReady(loadingState === true && localeCode === state.value.locale?.code)
  }

  //Private

  /**
  * please check `setMessages()`
  * here wait t load both local and api messages before setready here
  * @param ready 
  */
   const setReady = (ready=true) => {
    state.value.ready = ready
    nextTick(()=>{
      appStore.setGlobalsReady()
    })
  }

  /**
  * setter will also load local/api messages (cached)
  * and check if valid locale
  */
  const setLocale = (locale: Koddein.Locale) => {
    const isSame = locale.id === state.value.locale?.id
    if (isSame) return

    state.value.locale = locale
    const code = locale.code ?? ''
    if (!state.value.loaded[code]) {
      state.value.loaded[code] = 'loading'

      loadLocale(code)
        .then(()=>{
          state.value.loaded[code] = true
          nextTick(()=>{ setReady() })
        })

      // this.loadApiMessages(code)
      // this.loadLocalMessages(code)
    }
  }

  /**
   * @todo : wait global loaded (api call)
   * - fetch those messages
   * - merge with existant
   * - retrieve DATE_TIMES
   * - store both msg and dates
   */
   const loadLocale = async (code: string) => {
    // await until(toRef(globalsStore, 'ready') as boolean).toBe(true)
    await until(() => globalsStore.ready as boolean).toBe(true)

    const {DATE_TIME_FORMATS, ...messages} = (()=>{
      const fromApi = globalsStore.messages as Obj
      const fromLocal = toRaw(state.value.data.messages[code]) as Obj
      return merge({}, fromLocal, fromApi)
    })()

    state.value.data.messages[code] = messages as LocaleMessages
    state.value.data.datetimeFormats[code] = DATE_TIME_FORMATS
  }

  return {
    state,

    //getters
    locale, 
    locales,
    localeName: computed( () => state.value.locale?.name ?? 'en' ),
    otherLocales: computed<Optional<Koddein.Locale[]>>( () =>  filter(locales.value, (i) => i.id !== locale.value?.id) as Koddein.Locale[]),
    ready: computed( () => state.value.ready ),
    byName: (name:string) => computed<Optional<Koddein.Locale>>(() => byName(name)),
    byCode: (code:string) => computed<Optional<Koddein.Locale>>(() => byCode(code)),
    // because we may use no iso uri code...
    byUri: (uri:string) => computed<Optional<Koddein.Locale>>(() => byUri(uri)),
    byId: (id:number) => computed<Optional<Koddein.Locale>>(() => byId(id)),

    //actions
    setLocaleByName,
    setLocaleByCode,
    setLocaleByUri,
    setMessages,
  }

});

export const store = useStore(pinia);
export default store;

// private async loadLocalMessages(code: string) {
//   return new Promise<{messages:Obj, datetimeFormats:Obj}>(resolve=>{
//     switch(code){
//       case 'fr':
//         return resolve(loadFrMessages())
//       default:
//         return resolve(loadEnMessages())
//     }
//   })
//     .then(({ messages, datetimeFormats }) => {
//       this.setMessages(<LocaleMessages>messages, code, false)
//       this.state.data.datetimeFormats[code] = datetimeFormats
//     })
// }

// private async loadApiMessages(code: string) {
//   // await (async () => appStore.globalsReady.value
//   //   ? Promise.resolve(true)
//   //   : until(appStore.globalsReady).toBe(true)
//   // )()
//   await until(appStore.globalsReady).toBe(true)
//   return this.setMessages(<LocaleMessages>toRaw(globalsStore.getState().messages), code, true)
// }
